import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { FaEllipsisVertical } from "react-icons/fa6";
import { BsPencilFill, BsTrash3, BsArrowRepeat } from "react-icons/bs";
import { CiSettings } from "react-icons/ci";
import { LuSparkles, LuWorkflow, LuWrench } from "react-icons/lu";
import { AGENT_COLOR } from "../Agent/agentCatalog";

export const CARD_THEMES = {
    dark: {
        cardBg: "#111b21",
        headerBg: "#222e35",
        border: "#202c33",
        shadow: "#222e35",
        title: "#e9edef",
        text: "#e9edef",
        muted: "#9fb4b9",
        divider: "#222e35",
        menuBg: "#202c33",
        menuHover: "#2a3942",
        menuShadow: "#4e606b",
        iconButton: "#202c33",
        hoverShadow: "#455a64",
        activeBorder: "#00a884",
        activeShadow: "#005c4b",
    },
    light: {
        cardBg: "#ffffff",
        headerBg: "#f5fafd",
        border: "#e9eeef",
        shadow: "#dae2e4",
        title: "#455a64",
        text: "#37474f",
        muted: "#6d7d88",
        divider: "#e9eeef",
        menuBg: "#ffffff",
        menuHover: "#f5fafd",
        menuShadow: "#dae2e4",
        iconButton: "#e9eeef",
        hoverShadow: "#b7c6ca",
        activeBorder: "#00a884",
        activeShadow: "#4cae4c",
    },
};

export const isDarkMode = (isDark) => {
    const body = typeof document !== "undefined" ? document.getElementById("body-dark") : null;
    if (body) return body.classList.contains("dark");
    if (isDark === "true" || isDark === true) return true;
    if (isDark === "false" || isDark === false) return false;
    try {
        return Boolean(JSON.parse(localStorage.getItem("THEME")));
    } catch (error) {
        return false;
    }
};

export const resolveTheme = (isDark) => (isDarkMode(isDark) ? CARD_THEMES.dark : CARD_THEMES.light);

export const relativeDate = (value) => {
    const date = dayjs(value);
    if (!value || !date.isValid()) return "";
    const minutes = dayjs().diff(date, "minute");
    if (minutes < 1) return "ahora";
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "ayer";
    if (days < 30) return `hace ${days} días`;
    return `el ${date.locale("es").format("DD MMM YYYY")}`;
};

export const SCROLL_CLASS = "su-soft-scroll";

export const SCROLL_CSS = `
.${SCROLL_CLASS} { scrollbar-width: thin; scrollbar-color: #5bc0de transparent; }
.${SCROLL_CLASS}::-webkit-scrollbar { width: 6px; height: 6px; background: transparent; }
.${SCROLL_CLASS}::-webkit-scrollbar-track { background: transparent; }
.${SCROLL_CLASS}::-webkit-scrollbar-thumb { background-color: #5bc0de; border-radius: 6px; }
.${SCROLL_CLASS}::-webkit-scrollbar-thumb:hover { background-color: #46b8da; }
`;

const lockAncestorScroll = (node) => {
    const locked = [];
    const lock = (element) => {
        if (!element || locked.some((item) => item.element === element)) return;
        locked.push({ element, overflow: element.style.overflow, overflowY: element.style.overflowY });
        element.scrollTop = 0;
        element.style.overflow = "hidden";
        element.style.overflowY = "hidden";
    };
    let parent = node?.parentElement;
    while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (/(auto|scroll)/.test(`${style.overflow}${style.overflowY}`)) lock(parent);
        parent = parent.parentElement;
    }
    lock(document.body);
    lock(document.scrollingElement || document.documentElement);
    return () =>
        locked.reverse().forEach(({ element, overflow, overflowY }) => {
            element.style.overflow = overflow;
            element.style.overflowY = overflowY;
        });
};

export const useFillHeight = (gap = 12) => {
    const ref = useRef(null);
    const [height, setHeight] = useState(null);
    useLayoutEffect(() => {
        const unlock = lockAncestorScroll(ref.current);
        const update = () => {
            if (!ref.current) return;
            const top = ref.current.getBoundingClientRect().top;
            setHeight(Math.max(320, Math.floor(window.innerHeight - top - gap)));
        };
        update();
        const frame = window.requestAnimationFrame(update);
        window.addEventListener("resize", update);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener("resize", update);
            unlock();
        };
    }, [gap]);
    return [ref, height];
};

const stop = (event) => event.stopPropagation();

export const CardShell = ({ theme, onOpen, label, highlight = false, children }) => {
    const [hover, setHover] = useState(false);
    const shadowColor = highlight ? theme.activeShadow : hover ? theme.hoverShadow : theme.shadow;
    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={label}
            onClick={onOpen}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen();
                }
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                ...cardStyle(theme),
                cursor: "pointer",
                outline: "none",
                border: highlight ? `1px solid ${theme.activeBorder}` : undefined,
                transform: hover ? "translateY(-2px)" : "none",
                boxShadow: `${hover ? "3px 3px" : "2px 2px"} 0 0 ${shadowColor}`,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
            }}
        >
            {children}
        </div>
    );
};

export const StopClick = ({ children, style }) => (
    <div onClick={stop} onKeyDown={stop} style={style}>
        {children}
    </div>
);

export const BOT_TYPES = {
    transactional: { label: "Transaccional", color: "#F56F89" },
    flows: { label: "Flujos", color: "#9C27B0" },
    linear: { label: "Lineal", color: "#00A884" },
};

export const Pill = ({ color, children, icon: Icon }) => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "3px 9px",
            borderRadius: "12px",
            backgroundColor: color,
            color: "#ffffff",
            fontSize: "11px",
            fontWeight: 700,
            whiteSpace: "nowrap",
        }}
    >
        {Icon && <Icon style={{ fontSize: "10px" }} />}
        {children}
    </span>
);

export const CardMenu = ({ theme, items }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => {
            if (ref.current && !ref.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                type="button"
                aria-label="Más acciones"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
                style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: open ? theme.iconButton : "transparent",
                    color: theme.muted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                }}
            >
                <FaEllipsisVertical style={{ fontSize: "16px" }} />
            </button>
            {open && (
                <div
                    role="menu"
                    style={{
                        position: "absolute",
                        top: "38px",
                        right: 0,
                        zIndex: 20,
                        width: "200px",
                        padding: "6px",
                        borderRadius: "8px",
                        backgroundColor: theme.menuBg,
                        border: `1px solid ${theme.border}`,
                        boxShadow: `2px 2px 0 0 ${theme.menuShadow}, 0 12px 28px rgba(0, 0, 0, 0.25)`,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {items.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.label}
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                    setOpen(false);
                                    item.onClick();
                                }}
                                onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = theme.menuHover)}
                                onMouseLeave={(event) => (event.currentTarget.style.backgroundColor = "transparent")}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    textAlign: "left",
                                    padding: "9px 10px",
                                    borderRadius: "6px",
                                    border: "none",
                                    borderTop: item.danger ? `1px solid ${theme.divider}` : "none",
                                    backgroundColor: "transparent",
                                    color: item.danger ? "#ff5252" : theme.text,
                                    fontSize: "13px",
                                    cursor: "pointer",
                                }}
                            >
                                {Icon && <Icon style={{ fontSize: "15px", color: item.danger ? "#ff5252" : theme.muted }} />}
                                {item.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const cardStyle = (theme) => ({
    display: "flex",
    flexDirection: "column",
    borderRadius: "10px",
    backgroundColor: theme.cardBg,
    borderTop: `1px solid ${theme.border}`,
    boxShadow: `2px 2px 0 0 ${theme.shadow}`,
    minWidth: 0,
});

export const cardHeaderStyle = (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 12px 16px 20px",
    backgroundColor: theme.headerBg,
    borderRadius: "10px 10px 0 0",
});

const BotCard = ({ bot, isDark, onEdit, onDelete, onConfig, onClearStates, onToggle }) => {
    const theme = resolveTheme(isDark);
    const type = BOT_TYPES[bot?.type] || BOT_TYPES.linear;
    const description = (bot?.description || "").trim();
    const facts = [
        typeof bot?.nodesCount === "number" && { icon: LuWorkflow, text: `${bot.nodesCount} nodo${bot.nodesCount === 1 ? "" : "s"}` },
        bot?.agentConfig?.enabled && typeof bot?.agentToolsCount === "number" && {
            icon: LuWrench,
            text: `${bot.agentToolsCount} herramienta${bot.agentToolsCount === 1 ? "" : "s"} de IA`,
        },
    ].filter(Boolean);

    return (
        <CardShell theme={theme} highlight={bot.enabled} onOpen={() => onEdit(bot)} label={`Editar ${bot?.name || "bot"}`}>
            <div style={cardHeaderStyle(theme)}>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
                    <span
                        title={bot?.name}
                        style={{
                            fontFamily: "'Patua One', Georgia, serif",
                            fontSize: "17px",
                            color: theme.title,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {bot?.name}
                    </span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {bot.enabled && <Pill color="#005c4b">EN USO</Pill>}
                        <Pill color={type.color}>{type.label}</Pill>
                        {bot?.agentConfig?.enabled && (
                            <Pill color={AGENT_COLOR} icon={LuSparkles}>
                                Agente IA
                            </Pill>
                        )}
                    </div>
                </div>
                <StopClick>
                    <CardMenu
                        theme={theme}
                        items={[
                            { label: "Editar", icon: BsPencilFill, onClick: () => onEdit(bot) },
                            { label: "Configuración", icon: CiSettings, onClick: () => onConfig(bot) },
                            { label: "Limpiar estados", icon: BsArrowRepeat, onClick: () => onClearStates(bot._id) },
                            { label: "Eliminar", icon: BsTrash3, danger: true, onClick: () => onDelete(bot._id) },
                        ]}
                    />
                </StopClick>
            </div>

            <div style={{ padding: "18px 20px", flex: 1, display: "flex", flexDirection: "column", gap: "12px" }}>
                <span
                    style={{
                        fontSize: "13px",
                        lineHeight: 1.55,
                        color: description ? theme.text : theme.muted,
                        fontStyle: description ? "normal" : "italic",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {description || "Sin descripción"}
                </span>
                {facts.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "auto" }}>
                        {facts.map((fact) => {
                            const Icon = fact.icon;
                            return (
                                <span
                                    key={fact.text}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "5px 10px",
                                        borderRadius: "6px",
                                        backgroundColor: theme.iconButton,
                                        color: theme.muted,
                                        fontSize: "12px",
                                    }}
                                >
                                    <Icon style={{ fontSize: "13px" }} />
                                    {fact.text}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "13px 20px", borderTop: `1px solid ${theme.divider}` }}>
                <StopClick style={{ display: "flex", alignItems: "center" }}>
                    <input
                        type="checkbox"
                        className="cmn-toggle cmn-toggle-round-flat"
                        id={`bot-toggle-${bot._id}`}
                        name={bot._id}
                        defaultChecked={bot.enabled}
                        onChange={(event) => onToggle(event, bot)}
                    />
                    <label className="m-0" htmlFor={`bot-toggle-${bot._id}`}></label>
                </StopClick>
                <span style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.3px", color: bot.enabled ? "#00A884" : "#FF5252" }}>
                    {bot.enabled ? "ACTIVO" : "INACTIVO"}
                </span>
                <span style={{ marginLeft: "auto", minWidth: 0, fontSize: "12px", color: theme.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Creado {dayjs(bot.created).locale("es").format("DD MMM YYYY")}
                </span>
            </div>
        </CardShell>
    );
};

export default BotCard;
